/**
 * Firebase Performance Monitoring setup
 * Provides automatic and custom performance monitoring for the application
 */

import {
  getPerformance,
  trace as firebaseTrace,
  type PerformanceTrace,
} from 'firebase/performance';
import { app } from './firebase';
import { appConfig } from './env';

// Initialize Performance Monitoring only in production/staging
export const performance =
  appConfig.environment !== 'development' ? getPerformance(app) : null;

/**
 * Create a custom performance trace
 * @param name - Name of the trace (e.g., 'user_profile_load')
 * @returns Performance trace instance or null in development
 */
export function trace(name: string): PerformanceTrace | null {
  if (!performance) return null;

  try {
    return firebaseTrace(performance, name);
  } catch (error) {
    console.error('Failed to create performance trace:', error);
    return null;
  }
}

/**
 * Record a custom metric
 * @param name - Name of the metric
 * @param value - Value to record
 */
export function recordMetric(name: string, value: number): void {
  if (!performance) return;

  try {
    const customTrace = firebaseTrace(performance, 'custom_metrics');
    customTrace.putMetric(name, value);
    customTrace.start();
    customTrace.stop();
  } catch (error) {
    console.error('Failed to record metric:', error);
  }
}

/**
 * Measure async operation performance
 * @param name - Name of the operation
 * @param operation - Async function to measure
 * @returns Result of the operation
 */
export async function measureAsync<T>(
  name: string,
  operation: () => Promise<T>
): Promise<T> {
  const perfTrace = trace(name);

  if (!perfTrace) {
    return operation();
  }

  try {
    perfTrace.start();
    const result = await operation();
    perfTrace.stop();
    return result;
  } catch (error) {
    perfTrace.stop();
    throw error;
  }
}

/**
 * Measure sync operation performance
 * @param name - Name of the operation
 * @param operation - Function to measure
 * @returns Result of the operation
 */
export function measureSync<T>(name: string, operation: () => T): T {
  const perfTrace = trace(name);

  if (!perfTrace) {
    return operation();
  }

  try {
    perfTrace.start();
    const result = operation();
    perfTrace.stop();
    return result;
  } catch (error) {
    perfTrace.stop();
    throw error;
  }
}

/**
 * Custom metrics for key user flows
 */
export const UserFlowMetrics = {
  AUTH_FLOW_START: 'auth_flow_start',
  AUTH_FLOW_SUCCESS: 'auth_flow_success',
  AUTH_FLOW_ERROR: 'auth_flow_error',
  PROFILE_LOAD_START: 'profile_load_start',
  PROFILE_LOAD_SUCCESS: 'profile_load_success',
  PROFILE_LOAD_ERROR: 'profile_load_error',
  API_CALL_START: 'api_call_start',
  API_CALL_SUCCESS: 'api_call_success',
  API_CALL_ERROR: 'api_call_error',
} as const;

/**
 * Record user flow event
 * @param event - Event name from UserFlowMetrics
 * @param metadata - Optional metadata to include
 */
export function recordUserFlow(
  event: (typeof UserFlowMetrics)[keyof typeof UserFlowMetrics],
  metadata?: Record<string, string | number>
): void {
  if (!performance) return;

  try {
    const customTrace = firebaseTrace(performance, event);

    // Add metadata as custom attributes
    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        if (typeof value === 'string') {
          customTrace.putAttribute(key, value);
        } else if (typeof value === 'number') {
          customTrace.putMetric(key, Math.floor(value));
        }
      });
    }

    customTrace.start();
    customTrace.stop();
  } catch (error) {
    console.error('Failed to record user flow:', error);
  }
}
